import React from 'react';
import { MapPin, Calendar, DollarSign, Layers, ArrowRight, ShieldCheck } from 'lucide-react';
import { Language } from '../../types';
import { TRANSLATIONS } from '../../translations';

export interface OpportunityListing {
  id: string;
  refCode: string;
  category: string;
  headline: string;
  location: string;
  stage: string;
  projectType: string;
  budgetRange: string;
  seeking: string;
  timeframe: string;
  summary: string;
  status: 'Active' | 'Under Review' | 'Matched';
}

interface OpportunityCardProps {
  listing: OpportunityListing;
  language: Language;
  onExpressInterest: (refCode: string, category: string) => void;
}

export const OpportunityCard: React.FC<OpportunityCardProps> = ({
  listing,
  language,
  onExpressInterest
}) => {
  const t = TRANSLATIONS[language]?.exploreOpportunities?.directory?.card || {};

  return (
    <div className="bg-white border border-slate-200 hover:border-slate-400 transition-all shadow-sm flex flex-col h-full group">
      {/* Top Header */}
      <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 bg-slate-900 text-white text-[10px] font-bold uppercase tracking-widest">
            {listing.category}
          </span>
          <span className="text-[11px] font-mono text-slate-500 font-medium">
            {listing.refCode}
          </span>
        </div>
        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 uppercase tracking-wider">
          <ShieldCheck size={12} />
          {listing.status}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 flex-1 flex flex-col">
        <h4 className="text-lg font-heading uppercase tracking-wider text-slate-900 mb-4 leading-snug group-hover:text-blue-700 transition-colors">
          {listing.headline}
        </h4>

        {/* Parameters Grid */}
        <div className="grid grid-cols-2 gap-3 text-xs mb-6 p-3.5 bg-slate-50 border border-slate-150">
          <div>
            <span className="text-slate-400 text-[10px] uppercase tracking-wider block mb-0.5">
              {t.location || 'Location'}
            </span>
            <span className="font-semibold text-slate-800 flex items-center gap-1">
              <MapPin size={12} className="text-slate-400 shrink-0" />
              {listing.location}
            </span>
          </div>

          <div>
            <span className="text-slate-400 text-[10px] uppercase tracking-wider block mb-0.5">
              {t.stage || 'Stage'}
            </span>
            <span className="font-semibold text-slate-800 flex items-center gap-1">
              <Layers size={12} className="text-slate-400 shrink-0" />
              {listing.stage}
            </span>
          </div>

          <div>
            <span className="text-slate-400 text-[10px] uppercase tracking-wider block mb-0.5">
              {t.type || 'Project Type'}
            </span>
            <span className="font-semibold text-slate-800">
              {listing.projectType}
            </span>
          </div>

          <div>
            <span className="text-slate-400 text-[10px] uppercase tracking-wider block mb-0.5">
              {t.budget || 'Budget Range'}
            </span>
            <span className="font-semibold text-slate-800 flex items-center gap-1">
              <DollarSign size={12} className="text-slate-400 shrink-0" />
              {listing.budgetRange}
            </span>
          </div>
        </div>

        {/* Summary text */}
        <p className="text-xs text-slate-600 leading-relaxed mb-6 flex-1">
          {listing.summary}
        </p>

        {/* Seeking badge */}
        <div className="mb-6 p-2.5 bg-blue-50 border border-blue-100 text-xs">
          <span className="text-blue-900 font-bold uppercase tracking-wider text-[10px] block mb-0.5">
            {t.seeking || 'Seeking'}:
          </span>
          <span className="text-blue-950 font-medium">{listing.seeking}</span>
        </div>

        {/* Action button */}
        <button
          onClick={() => onExpressInterest(listing.refCode, listing.category)}
          className="w-full bg-slate-900 text-white py-3 px-4 uppercase tracking-widest text-xs font-bold hover:bg-slate-800 transition-colors flex justify-center items-center gap-2 mt-auto"
        >
          {t.expressInterest || 'Express Interest (EOI)'} <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
};
